import React, {
  memo,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import ReactDOM from "react-dom";
import { toEmojiChar } from "../../../utils/messageNormalizer";
import AppIcon from "../../../components/ui/AppIcon";
import EmojiPickerLib from "emoji-picker-react";
import { SmilePlus } from "lucide-react";

const QUICK_REACTIONS = ["👍", "✔", "🔥"];

// ── Emoji Picker for more reactions ─────────────────────────────────────────
function MoreEmojiPicker({ anchorRef, onSelect, onClose, pickerRef }) {
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  // Shrink the picker on narrow screens so it never overflows the viewport.
  const pickerW = Math.min(320, window.innerWidth - 24);
  const pickerH = 380;

  useLayoutEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const margin = 12;

    // Preferred: above the anchor button
    let top = rect.top - pickerH - 8;
    let left = rect.right - pickerW;

    // Flip to below if it overflows top
    if (top < margin) {
      top = rect.bottom + 8;
    }

    // Clamp horizontal
    if (left < margin) left = margin;
    if (left + pickerW > window.innerWidth - margin) {
      left = window.innerWidth - pickerW - margin;
    }

    setCoords({ top, left });
  }, [anchorRef]);

  return createPortal(
    <div
      ref={pickerRef}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        zIndex: 99999,
      }}
    >
      <EmojiPickerLib
        onEmojiClick={(emojiData) => {
          onSelect(emojiData.emoji);
          // onClose();
        }}
        autoFocusSearch
        lazyLoadEmojis
        skinTonesDisabled
        height={380}
        width={pickerW}
        searchPlaceholder="Search emoji..."
        previewConfig={{ showPreview: false }}
      />
    </div>,
    document.body,
  );
}

const ReactionPopover = memo(
  ({
    anchorRef,
    messageId,
    onReaction,
    onPin,
    onDelete,
    onThread,
    onEdit,
    isOwn,
    isVisible,
    onClose,
  }) => {
    const popoverRef = useRef(null);
    const moreEmojiBtnRef = useRef(null);
    const moreEmojiPickerRef = useRef(null);
    const [showMoreEmoji, setShowMoreEmoji] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

    // ── Compute position relative to anchor ─────────────────────────────────
    useLayoutEffect(() => {
      if (!isVisible || !anchorRef?.current) return;

      const reposition = () => {
        const anchor = anchorRef.current;
        const popover = popoverRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const popoverW = popover ? popover.offsetWidth : 320;
        const popoverH = popover ? popover.offsetHeight : 52;

        const margin = 12;

        // Find the chat container to respect sidebars (Details panel, channel list)
        const chatContainer = anchor.closest('.js-chat-main-container');
        const boundary = chatContainer
          ? chatContainer.getBoundingClientRect()
          : {
            left: 0,
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
            width: window.innerWidth,
            height: window.innerHeight
          };

        const viewRight = boundary.right;
        const viewLeft = boundary.left;
        const viewBottom = boundary.bottom;
        const viewTop = boundary.top;

        // Preferred: centered below the anchor
        let top = rect.bottom + 8;
        let left = rect.left + rect.width / 2 - popoverW / 2;

        // 1. Horizontal clamping (respect container boundaries)
        if (left + popoverW > viewRight - margin) {
          left = viewRight - popoverW - margin;
        }
        if (left < viewLeft + margin) {
          left = viewLeft + margin;
        }

        // 2. Vertical flipping (if it overflows container bottom)
        if (top + popoverH > viewBottom - margin) {
          // If we can't fit it below, try above the anchor
          const topAbove = rect.top - popoverH - 8;
          if (topAbove > viewTop + margin) {
            top = topAbove;
          } else {
            // If it doesn't fit above either, just clamp it to bottom
            top = viewBottom - popoverH - margin;
          }
        }

        setCoords({ top, left, ready: true });
      };

      reposition();
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
      return () => {
        window.removeEventListener("resize", reposition);
        window.removeEventListener("scroll", reposition, true);
      };
    }, [isVisible, anchorRef]);

    // ── Close on Escape ───────────────────────────────────────────────────────
    useEffect(() => {
      if (!isVisible) return;
      const handleKey = (e) => {
        if (e.key === "Escape") onClose();
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }, [isVisible, onClose]);

    // ── Close on outside click ────────────────────────────────────────────────
    useEffect(() => {
      if (!isVisible) return;
      const handleOutside = (e) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target) &&
          anchorRef?.current &&
          !anchorRef.current.contains(e.target) &&
          (!moreEmojiPickerRef.current ||
            !moreEmojiPickerRef.current.contains(e.target)) &&
          (!moreEmojiBtnRef.current ||
            !moreEmojiBtnRef.current.contains(e.target))
        ) {
          onClose();
        }
      };
      // Slight delay so the opening click doesn't immediately close it
      const id = setTimeout(() => {
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("touchstart", handleOutside);
      }, 50);
      return () => {
        clearTimeout(id);
        document.removeEventListener("mousedown", handleOutside);
        document.removeEventListener("touchstart", handleOutside);
      };
    }, [isVisible, onClose, anchorRef]);

    // ── Close more emoji picker on outside click ─────────────────────────────
    useEffect(() => {
      if (!showMoreEmoji) return;
      const handleOutside = (e) => {
        if (
          moreEmojiPickerRef.current &&
          !moreEmojiPickerRef.current.contains(e.target) &&
          moreEmojiBtnRef.current &&
          !moreEmojiBtnRef.current.contains(e.target)
        ) {
          setShowMoreEmoji(false);
        }
      };
      const id = setTimeout(() => {
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("touchstart", handleOutside);
      }, 50);
      return () => {
        clearTimeout(id);
        document.removeEventListener("mousedown", handleOutside);
        document.removeEventListener("touchstart", handleOutside);
      };
    }, [showMoreEmoji]);

    if (!isVisible) return null;

    const handleReaction = (emoji) => {
      onReaction?.(emoji);
      onClose();
    };

    const handlePin = () => {
      onPin?.();
      onClose();
    };

    const handleDelete = () => {
      onDelete?.();
      onClose();
    };

    const handleThread = () => {
      onThread?.();
      onClose();
    };

    const handleEdit = () => {
      onEdit?.();
      onClose();
    };

    const popover = (
      <div
        ref={popoverRef}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          zIndex: 99999,
          opacity: coords.ready ? 1 : 0,
          transform: coords.ready ? "scale(1)" : "scale(0.92)",
          transition: "opacity 150ms ease-out, transform 150ms ease-out",
          pointerEvents: "auto",
        }}
        role="dialog"
        aria-label="Reaction options"
        aria-modal="false"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1 bg-app-surface backdrop-blur-xl border border-border-subtle rounded-full px-2 py-1.5 shadow-modal">
          {/* Quick emoji reactions */}
          {QUICK_REACTIONS.map((emoji) => {
            const displayEmoji = toEmojiChar(emoji);
            return (
              <button
                key={emoji}
                type="button"
                className="w-9 h-9 flex items-center justify-center text-xl rounded-full transition-all duration-150 hover:scale-125 active:scale-95 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-ink/30"
                onClick={() => handleReaction(displayEmoji)}
                aria-label={`React with ${displayEmoji}`}
                title={displayEmoji}
              >
                {/* ✅ FIX: always render converted emoji character */}
                {displayEmoji}
              </button>
            );
          })}

          {/* More reactions button */}
          <div className="relative">
            <button
              ref={moreEmojiBtnRef}
              type="button"
              className="w-9 h-9 flex items-center justify-center text-lg rounded-full transition-all duration-150 text-text-secondary hover:bg-black/5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-ink/30"
              onClick={() => setShowMoreEmoji((prev) => !prev)}
              aria-label="More reactions"
              title="More reactions"
            >
              <SmilePlus />
            </button>
            {showMoreEmoji && (
              <MoreEmojiPicker
                anchorRef={moreEmojiBtnRef}
                pickerRef={moreEmojiPickerRef}
                onSelect={(emoji) => {
                  onReaction?.(emoji);
                  // setShowMoreEmoji(false);
                }}
                onClose={() => setShowMoreEmoji(false)}
              />
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-5 bg-border-subtle mx-0.5" />

          {/* Pin */}
          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 text-text-secondary hover:bg-black/5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-ink/30"
            onClick={handlePin}
            aria-label="Pin message"
            title="Pin"
          >
            <AppIcon name="PinIcon" size={16} />
          </button>

          {/* Delete — own messages only */}
          {isOwn && (
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 text-text-secondary hover:bg-danger/10 hover:text-danger focus:outline-none focus:ring-2 focus:ring-ink/30"
              onClick={handleDelete}
              aria-label="Delete message"
              title="Delete"
            >
              <AppIcon name="TrashIcon" size={16} />
            </button>
          )}

          {/* Edit — own messages only */}
          {isOwn && onEdit && (
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 text-text-secondary hover:bg-black/5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-ink/30"
              onClick={handleEdit}
              aria-label="Edit message"
              title="Edit"
            >
              <AppIcon name="PencilIcon" size={16} />
            </button>
          )}

          {/* Thread — always visible; opens the thread panel for this message */}
          {onThread && (
            <>
              <div className="w-px h-5 bg-border-subtle mx-0.5" />
              <button
                type="button"
                className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 text-text-secondary hover:bg-black/5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-ink/30"
                onClick={handleThread}
                aria-label="Reply in thread"
                title="Reply in thread"
              >
                <AppIcon name="ChatBubbleLeftRightIcon" size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );

    return createPortal(popover, document.body);
  },
);

ReactionPopover.displayName = "ReactionPopover";
export default ReactionPopover;
